import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CashBalancesState as CashBalanceState, CashBalancesInstance, cashBalancesWidgetAdapter } from '../reducers/cash-balances.reducers';
import { distinctMapObj, distinct, groupBy, sum } from '@common/lang/lang';
export const selectCashBalancesState = createFeatureSelector<CashBalanceState>('cashBalances');
import { QueryBuilderConfig, RuleSet, Rule } from 'ngx-angular-query-builder';
import { CashBalanceFieldNames } from '../cash-balance-field-identifiers';
import { CashBalancesModel } from '../models/cash-balances.model';
import { CashBalancesFilterFieldProvider } from '../filter/cash-balances-filter-field-provider';
import { AdminCashBalancesFilterFieldProvider } from '../filter/admin-cash-balances-filter-field-provider';
import { SubLedgerModel } from '../models/sub-ledger.model';
import * as widgetConfigSelectors from '@widget-configs/selectors/widget-configs';
import { WidgetConfiguration } from '@widget-configs/models/widget-configuration';
import { CashBalanceLabelService } from '../services/cash-balance-label.service'
import { CashBalanceGroupTypeProvider } from '../services/cash-balance-group-type-provider.service'
import { GroupedChart, GroupedChartItem } from '@common/models/grouped-chart';
import { QueryBuilderConfigProvider } from '@common/filter/query-builder-config-provider';
import { GroupedTable } from '@common/models/grouped-table';
import * as filterSelectors from '@common/filter/selectors/filter.selectors';
import * as filterReducers from '@common/filter/reducers/filter.reducers';
import * as filterUtils from '@common/filter/filter-utils';
import { FilterListItem } from '@common/filter/models/filter-list-item.model';
import { CashBalanceTypes } from '../constants/cash-balance-types';
import { StandardFilterStrategy } from '@common/filter/standard-filter-strategy';

export const {
  selectIds,
  selectEntities,
  selectAll: selectAllDiaryWidgetsData,
  selectTotal,
} = cashBalancesWidgetAdapter.getSelectors(selectCashBalancesState);

export const selectEntityByWidgetId = (widgetId: string) => createSelector(selectEntities, (entities) => entities[widgetId.toString()]);

export function getFilter(widgetId: string) {
  return createSelector([filterSelectors.selectByWidgetId(widgetId), selectEntityByWidgetId(widgetId.toString())],
    (filter: filterReducers.WidgetFilterInstance, cashBalanceInstance: CashBalancesInstance) => {
      if (filter && cashBalanceInstance) {
        return filter.filter || filterUtils.getDefaultFilter();
      }
      return filterUtils.getDefaultFilter();
    });
}


export function getView(widgetId: string) {
  return createSelector(
    [selectEntityByWidgetId(widgetId.toString())],
    (cashBalanceInstance: CashBalancesInstance) => {
      return cashBalanceInstance.view;
    }
  );
}

export function getChartGroupType(widgetId: string) {
  return createSelector(
    [selectEntityByWidgetId(widgetId.toString())],
    (cashBalanceInstance: CashBalancesInstance) => {
      const groupTypeProvider = new CashBalanceGroupTypeProvider();
      const groupTypes = groupTypeProvider.getChartGroupTypes();
      if (cashBalanceInstance.groupByField1 && cashBalanceInstance.groupByField2) {
        return groupTypes.filter(x => {
          return x.GroupByField1 === cashBalanceInstance.groupByField1 &&
            x.GroupByField2 === cashBalanceInstance.groupByField2;
        })[0];
      } else {
        return groupTypes[0];
      }
    }
  );
}

export function getSubLedgers() {
  return createSelector(
    [selectCashBalancesState],
    (cashBalance: CashBalanceState) => {
      if (cashBalance != null) {
        return cashBalance.subLedgers;
      }
    }
  );
}

export function getSubLedgersQueryConfig() {
  return createSelector(
    [getSubLedgers()],
    (subLedgers: SubLedgerModel[]) => {
      if (subLedgers) {
        const adminCashBalancesFilterFieldProvider = getAdminCashBalancesFilterFieldProvider(subLedgers);
        const adminCashBalancesQueryConfigBuilder = new QueryBuilderConfigProvider(adminCashBalancesFilterFieldProvider);
        return adminCashBalancesQueryConfigBuilder.getQueryBuilderConfig() as QueryBuilderConfig;
      }
      return undefined as QueryBuilderConfig;
    });
}

export function getQueryConfig(widgetConfigId: number, filterFieldProviderFactoryCallback: CashBalancesFilterFieldProviderFactoryCallback) {
  return createSelector(
    [selectCashBalancesState, getDefaultFilteredCashBalances(widgetConfigId, filterFieldProviderFactoryCallback)],
    (cashBalanceState: CashBalanceState, filteredCashBalances: CashBalancesModel[]) => {
      if (cashBalanceState && cashBalanceState.cashBalances) {

        const cashBalancesFilterFieldProvider = filterFieldProviderFactoryCallback(filteredCashBalances);
        const cashBalancesQueryConfigBuilder = new QueryBuilderConfigProvider(cashBalancesFilterFieldProvider);
        return cashBalancesQueryConfigBuilder.getQueryBuilderConfig() as QueryBuilderConfig;
      }

      return { fields: {} } as QueryBuilderConfig;
    }
  );
}

export function getSysAdminFilterStrings(widgetConfigId: number) {
  return createSelector(
    [widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId)],
    (widgetConfiguration: WidgetConfiguration) => {
      if (widgetConfiguration && widgetConfiguration.filter && widgetConfiguration.filter.rules && widgetConfiguration.filter.rules.length > 0) {
        const result: string[] = [];
        widgetConfiguration.filter.rules.forEach(x => {
          if (!(x as RuleSet).rules) {
            const rule = x as Rule;
            let condition = '';
            if (result.length > 0) {
              condition = `${widgetConfiguration.filter.condition} `;
            }
            result.push(`${condition}${rule.field} ${rule.operator} ${rule.value}`);
          }
        });
        return result;
      }
      return undefined;
    }
  );
}

export function getDefaultFilteredCashBalances(widgetConfigId: number, filterFieldProviderFactoryCallback: CashBalancesFilterFieldProviderFactoryCallback) {
  return createSelector(
    [selectCashBalancesState, widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId)],
    (cashBalanceState: CashBalanceState, widgetConfiguration: WidgetConfiguration) => {
      if (cashBalanceState && cashBalanceState.cashBalances && widgetConfiguration) {
        const cashBalancesFilterFieldProvider = filterFieldProviderFactoryCallback(cashBalanceState.cashBalances);
        const filterStrategy = new StandardFilterStrategy<CashBalancesModel>(cashBalancesFilterFieldProvider);
        return filterStrategy.applyFilter(cashBalanceState.cashBalances, widgetConfiguration.filter);
      }

      return [] as CashBalancesModel[];
    }
  );
}

export function getFilteredCashBalances(widgetId: string, widgetConfigId: number, filterFieldProviderFactoryCallback: CashBalancesFilterFieldProviderFactoryCallback) {
  return createSelector(
    [selectCashBalancesState, selectEntityByWidgetId(widgetId.toString()), widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId), getFilter(widgetId)],
    (cashBalanceState: CashBalanceState, cashBalanceInstance: CashBalancesInstance, widgetConfiguration: WidgetConfiguration, filter: RuleSet) => {
      if (cashBalanceState && cashBalanceState.cashBalances && widgetConfiguration) {
        const cashBalancesFilterFieldProvider = filterFieldProviderFactoryCallback(cashBalanceState.cashBalances);
        const filterStrategy = new StandardFilterStrategy<CashBalancesModel>(cashBalancesFilterFieldProvider);
        const cashBalancesFilteredByCurrency = cashBalanceState.cashBalances.filter(a => a.CurrencyCode === cashBalanceInstance.selectedCurrency);
        return filterStrategy.applyFilterWithDefault(cashBalancesFilteredByCurrency, filter, widgetConfiguration.filter);

      }
      return [] as CashBalancesModel[];
    }
  );
}




export function getAdminCashBalancesFilterFieldProvider(subLedgers: Array<SubLedgerModel>): AdminCashBalancesFilterFieldProvider {
  return new AdminCashBalancesFilterFieldProvider(subLedgers, CashBalanceTypes.AllTypes);
}

export function getCashBalanceDataForChart(widgetId: string, widgetConfigId: number, colors: Array<string>, filterFieldProviderFactoryCallback: CashBalancesFilterFieldProviderFactoryCallback) {
  return createSelector(
    [selectCashBalancesState,
      selectEntityByWidgetId(widgetId.toString()),
      widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId),
      getFilteredCashBalances(widgetId, widgetConfigId, filterFieldProviderFactoryCallback)],
    (cashBalanceState: CashBalanceState, cashBalanceInstance: CashBalancesInstance, widgetConfiguration: WidgetConfiguration, filteredCashBalances: CashBalancesModel[]) => {

      if (cashBalanceState && cashBalanceState.cashBalances) {

        const balancesGroupedByField2 = groupBy(filteredCashBalances, cashBalanceInstance.groupByField2);
        const distinctValuesInGroupByField1 = <Array<string>>distinct(filteredCashBalances, cashBalanceInstance.groupByField1);
        let colourIndex = 0;

        const result: GroupedChart = { labels: [], datasets: [] };

        result.labels = distinctValuesInGroupByField1;

        for (const propName in balancesGroupedByField2) {

          const itemToAdd: GroupedChartItem = {
            label: propName, data: [], backgroundColor: []
          };

          distinctValuesInGroupByField1.forEach(distinctName => {

            const itemsFilteredByDistinctName = balancesGroupedByField2[propName].filter(property => {
              return property[cashBalanceInstance.groupByField1] === distinctName;
            });

            if (itemsFilteredByDistinctName.length > 0) {
              const summedValue = sum(itemsFilteredByDistinctName, CashBalanceFieldNames.BalanceField);
              itemToAdd.data.push(precisionRound(summedValue, 2));
            } else {
              itemToAdd.data.push(0);
            }

            itemToAdd.backgroundColor.push(colors[colourIndex]);
          });
          result.datasets.push(itemToAdd);

          if (colourIndex === colors.length - 1) {
            colourIndex = 0;
          }
          else {
            colourIndex++;
          }

        }
        return result;

      }
      return undefined;
    }
  );
}

export function getTotalRow(widgetId: string, widgetConfigId: number, filterFieldProviderFactoryCallback: CashBalancesFilterFieldProviderFactoryCallback) {
  return createSelector(
    [getCashBalanceDataForTable(widgetId, widgetConfigId, filterFieldProviderFactoryCallback)], (cashBalancesTableData) => {
      const totalRow: any = {};
      if (cashBalancesTableData?.xAxisLabels) {
        cashBalancesTableData.xAxisLabels.forEach(distinctName => {
          const summedValue = precisionRound(sum(cashBalancesTableData.rows, distinctName), 2);

          totalRow[distinctName] = summedValue;

        });
      }
      return totalRow;
    });
}
export function getCashBalanceDataForTable(widgetId: string, widgetConfigId: number, filterFieldProviderFactoryCallback: CashBalancesFilterFieldProviderFactoryCallback) {
  return createSelector(
    [selectCashBalancesState,
      selectEntityByWidgetId(widgetId.toString()),
      widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId),
      getFilteredCashBalances(widgetId, widgetConfigId, filterFieldProviderFactoryCallback)],
    (cashBalanceState: CashBalanceState, cashBalanceInstance: CashBalancesInstance, widgetConfiguration: WidgetConfiguration, filteredCashBalances: CashBalancesModel[]) => {
      if (cashBalanceState && cashBalanceState.cashBalances && cashBalanceState.cashBalances.length > 0) {


        const cashBalanceLabelService: CashBalanceLabelService = new CashBalanceLabelService();
        const column1Header = cashBalanceLabelService.getLabelTextForField(cashBalanceInstance.groupByField2);

        const distinctValuesInGroupByField1 = distinct<string, CashBalancesModel>(filteredCashBalances, cashBalanceInstance.groupByField1);
        const balancesGroupedByField2 = groupBy(filteredCashBalances, cashBalanceInstance.groupByField2);
        const result: GroupedTable = { xAxisLabels: [column1Header], rows: [] };

        result.xAxisLabels.push(...distinctValuesInGroupByField1);
        result.xAxisLabels.push('Total');

        for (const name in balancesGroupedByField2) {

          const itemToAdd: any = {};
          itemToAdd[column1Header] = name;

          let rowRunningTotal = 0;

          distinctValuesInGroupByField1.forEach(distinctName => {

            const itemsFilteredByDistinctName = balancesGroupedByField2[name].filter(property => {
              return property[cashBalanceInstance.groupByField1] === distinctName;
            });

            if (itemsFilteredByDistinctName.length > 0) {
              const summedValue = precisionRound(sum(itemsFilteredByDistinctName, CashBalanceFieldNames.BalanceField), 2);
              itemToAdd[distinctName] = summedValue;
              rowRunningTotal += summedValue;
            } else {
              itemToAdd[distinctName] = 0;
            }
            itemToAdd.Total = precisionRound(rowRunningTotal, 2);
          });

          result.rows.push(itemToAdd);
        }

        return result;

      }
      return undefined;
    }
  );
}

function precisionRound(number, precision) {
  const factor = Math.pow(10, precision);
  return Math.round(number * factor) / factor;
}


// Refactoring needs to be done to unpick the CashBalancesFilterFieldProvider from the selectors
// first step is providing a callback to allow the construction to take place outside
export type CashBalancesFilterFieldProviderFactoryCallback = (cashBalances: Array<CashBalancesModel>) => CashBalancesFilterFieldProvider;

export function makeCashBalancesFilterFieldProviderCallback(effectiveProduct: string, currentDashboard: string):
  CashBalancesFilterFieldProviderFactoryCallback {

  return (cashBalances: Array<CashBalancesModel>) => {

    return new CashBalancesFilterFieldProvider(cashBalances, effectiveProduct, currentDashboard);

  }

}

export function mapToFilterListItem(cashBalances: CashBalancesModel[], valueCallback, nameCallback = undefined): FilterListItem[] {


  return distinctMapObj<CashBalancesModel, FilterListItem>(cashBalances,
    (x: CashBalancesModel) => {
      return {
        name: nameCallback != undefined ? nameCallback(x) : valueCallback(x),
        value: valueCallback(x)
      } as FilterListItem;
    },
    (x: FilterListItem) => x.value);
}
